import React from "react";
import { DataTypes } from "../../Generic/DetailsPageComponents/parseDetailsConfig";
import { formatDisplayDate } from "../../util/date.util";
import {
	ActionTargetLabels,
	ActionTypeLabels,
	ConditionOperatorLabels,
	ConditionSubjectLabels,
	OfferActionField,
	OfferChannelLabels,
	OfferConditionField,
	OfferField,
	OfferMappingField,
	OfferRuleField,
	OfferTermsBlockField,
	TermsConditionType,
	TermsConditionTypeLabels,
} from "../constants";
import {
	displayValue,
	formatDate,
	formatMappingDisplay,
	getListItems,
	getParagraphText,
	isRenderableTermsBlock,
} from "../utils/offerDisplay";

/** Looks up a label map value, or "-" when empty. */
const labelFromMap = (map, value) => {
	if (value === "" || value === null || value === undefined) return "-";
	return map[value] || String(value);
};

export const getOfferDetailsConfig = () => ({
	basicInformation: {
		disableCollapse: true,
		title: "Basic Information",
		type: DataTypes.Object,
		children: [
			{
				title: "ID",
				value: (offer) => displayValue(offer.id),
			},
			{
				title: "Title",
				value: (offer) => displayValue(offer[OfferField.TITLE]),
			},
			{
				title: "Code",
				value: (offer) => displayValue(offer[OfferField.OFFER_CODE]),
			},
			{
				title: "Description",
				value: (offer) => displayValue(offer[OfferField.DESCRIPTION]),
			},
			{
				title: "Type",
				value: (offer) => displayValue(offer[OfferField.OFFER_TYPE]),
			},
			{
				title: "Sub Type",
				omit: (offer) => !offer[OfferField.SUB_OFFER_TYPE],
				value: (offer) => offer[OfferField.SUB_OFFER_TYPE],
			},
			{
				title: "Offer Group ID",
				value: (offer) => displayValue(offer[OfferField.OFFER_GROUP_ID]),
			},
			{
				title: "Channel",
				value: (offer) =>
					labelFromMap(OfferChannelLabels, offer[OfferField.CHANNEL]),
			},
			{
				title: "Priority",
				value: (offer) => displayValue(offer[OfferField.PRIORITY]),
			},
			{
				title: "Period",
				value: (offer) =>
					`${formatDate(offer[OfferField.START_DATE])} – ${formatDate(
						offer[OfferField.END_DATE]
					)}`,
			},
			{
				title: "Max Usage",
				value: (offer) => displayValue(offer[OfferField.MAX_USAGE_COUNT]),
			},
			{
				title: "Individual Max Usage",
				value: (offer) =>
					displayValue(offer[OfferField.INDIVIDUAL_MAX_USAGE_COUNT]),
			},
			{
				title: "Usage Count",
				value: (offer) => displayValue(offer[OfferField.USAGE_COUNT]),
			},
			{
				title: "Version",
				value: (offer) => displayValue(offer[OfferField.VERSION]),
			},
			{
				title: "Created",
				value: (offer) => formatDisplayDate(offer.createdAt),
			},
			{
				title: "Updated",
				value: (offer) => formatDisplayDate(offer.updatedAt),
			},
		],
	},
	ruleMeta: {
		initiallyExpanded: true,
		title: "Rules",
		type: DataTypes.Object,
		children: [
			{
				title: "Rule Description",
				value: (offer) =>
					displayValue(offer[OfferField.RULE]?.[OfferRuleField.DESCRIPTION]),
			},
			{
				title: "Operator",
				value: (offer) =>
					offer[OfferField.RULE]?.[OfferRuleField.OPERATOR] || "-",
			},
		],
	},
	conditions: {
		initiallyExpanded: true,
		title: "Conditions",
		type: DataTypes.List,
		skipIfEmpty: true,
		retrieveListData: (offer) =>
			offer[OfferField.RULE]?.[OfferRuleField.CONDITIONS] || [],
		children: [
			{
				title: "Subject",
				value: (condition) =>
					labelFromMap(
						ConditionSubjectLabels,
						condition[OfferConditionField.SUBJECT]
					),
			},
			{
				title: "Operator",
				value: (condition) =>
					labelFromMap(
						ConditionOperatorLabels,
						condition[OfferConditionField.OPERATOR]
					),
			},
			{
				title: "Value",
				value: (condition) => displayValue(condition[OfferConditionField.VALUE_LABEL] || condition[OfferConditionField.VALUE]),
			},
		],
	},
	action: {
		initiallyExpanded: true,
		title: "Action",
		type: DataTypes.List,
		skipIfEmpty: true,
		retrieveListData: (offer) => {
			const action = offer[OfferField.RULE]?.[OfferRuleField.ACTION];
			if (!action?.[OfferActionField.TYPE]) return [];
			return [action];
		},
		children: [
			{
				title: "Type",
				value: (action) =>
					labelFromMap(ActionTypeLabels, action[OfferActionField.TYPE]),
			},
			{
				title: "Value",
				value: (action) => displayValue(action[OfferActionField.VALUE]),
			},
			{
				title: "Target",
				value: (action) =>
					labelFromMap(ActionTargetLabels, action[OfferActionField.TARGET]),
			},
			{
				title: "Min Amount Limit",
				value: (action) =>
					displayValue(action[OfferActionField.MIN_AMOUNT_LIMIT]),
			},
			{
				title: "Max Amount Limit",
				value: (action) =>
					displayValue(action[OfferActionField.MAX_AMOUNT_LIMIT]),
			},
		],
	},
	mapping: {
		initiallyExpanded: true,
		title: "Mapping",
		type: DataTypes.Object,
		children: [
			{
				title: "Menus",
				value: (offer) =>
					formatMappingDisplay(
						offer[OfferField.OFFER_MAPPING]?.[OfferMappingField.MENU_IDS]
					),
			},
			{
				title: "Restaurants",
				value: (offer) =>
					formatMappingDisplay(
						offer[OfferField.OFFER_MAPPING]?.[OfferMappingField.RESTAURANT_IDS],
						{ preferId: true }
					),
			},
			{
				title: "Users",
				value: (offer) =>
					formatMappingDisplay(
						offer[OfferField.OFFER_MAPPING]?.[OfferMappingField.USER_IDS]
					),
			},
		],
	},
	terms: {
		initiallyExpanded: true,
		title: "Terms & Conditions",
		type: DataTypes.List,
		skipIfEmpty: true,
		retrieveListData: (offer) =>
			(offer[OfferField.TERMS_CONDITION] || []).filter(isRenderableTermsBlock),
		children: [
			{
				title: "Type",
				value: (block) =>
					labelFromMap(
						TermsConditionTypeLabels,
						block[OfferTermsBlockField.TYPE]
					),
			},
			{
				title: "Content",
				value: (block) => {
					const type = block[OfferTermsBlockField.TYPE];
					const content = block[OfferTermsBlockField.CONTENT];
					if (type === TermsConditionType.PARAGRAPH) {
						return getParagraphText(content);
					}
					const items = getListItems(content);
					return (
						<ol>
							{items.map((item, index) => (
								<li key={`terms-item-${index}`}>{item}</li>
							))}
						</ol>
					);
				},
			},
		],
	},
});
